import { memo } from "react";
import { useTranslation } from "react-i18next";
import { AvatarBubble } from "@wedding/slideshow-ui";
import type { RecentUploader } from "../../hooks/use-live-reactions";
import { cn } from "../../lib/utils";

interface SocialTickerProps {
  title: string;
  dateLine: string | null;
  uploaderName: string | null;
  /** Current slide uploader's guest id — dedups the credit vs the pile by identity. */
  uploaderId: string | null;
  recentUploaders: RecentUploader[];
  projector: boolean;
  /** Rendered inside a shared positioned wrapper (presence column) — skip own absolute placement. */
  inline?: boolean;
}

/**
 * Editorial lower-left title block — no card, sits directly on the stage scrim.
 * A thin accent rule, a dramatic serif title, tracked metadata, and a small
 * credit + recent-contributor stack (all one AvatarBubble language).
 */
function SocialTickerImpl({ title, dateLine, uploaderName, uploaderId, recentUploaders, projector, inline }: SocialTickerProps) {
  const { t } = useTranslation();
  // The current slide's uploader already shows as the "sharedBy" credit — drop
  // them from the contributor pile so the SAME person never appears twice.
  // Prefer identity (guest id); fall back to name for pre-deploy DO snapshots.
  const pileUploaders = recentUploaders.filter((u) =>
    uploaderId ? u.guestId !== uploaderId : u.name !== uploaderName,
  );
  return (
    <div
      className={cn(
        "pointer-events-none",
        !inline && "absolute bottom-[3.5vmin] left-[3.5vmin] z-20 max-w-[55vw] md:max-w-[62vw]",
      )}
    >
      <div className="flex items-stretch gap-4">
        <div className="w-1 self-stretch min-h-[3.5rem] rounded-full bg-primary shrink-0" />
        <div className="min-w-0">
          <h1
            className={cn(
              "font-serif italic text-white leading-[1.0] drop-shadow-[0_2px_16px_rgba(0,0,0,0.75)] line-clamp-2",
              projector ? "text-5xl md:text-7xl lg:text-8xl" : "text-3xl md:text-5xl",
            )}
          >
            {title}
          </h1>
          {dateLine && (
            <p
              className={cn(
                "mt-2 text-white/65 uppercase tracking-[0.28em] font-medium",
                projector ? "text-sm md:text-lg" : "text-[11px] md:text-sm",
              )}
            >
              {dateLine}
            </p>
          )}

          <div className="mt-3.5 flex items-center gap-4 flex-wrap">
            {uploaderName && (
              <div className="flex items-center gap-2.5">
                <AvatarBubble name={uploaderName} className="size-8 text-xs ring-2 ring-white/15" />
                <p className="text-white/75 text-sm md:text-base">
                  {t("slideshow.sharedBy", { name: uploaderName })}
                </p>
              </div>
            )}

            {pileUploaders.length > 0 && (
              <div className="flex flex-col items-start gap-1">
                <div className="flex -space-x-2.5">
                  {pileUploaders.slice(0, 4).map((u) => (
                    <AvatarBubble key={u.id} name={u.name} className="size-7 text-[11px] ring-2 ring-white/15" />
                  ))}
                </div>
                <span className="text-white/40 text-xs uppercase tracking-widest">
                  {t("slideshow.memoriesInSync")}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export const SocialTicker = memo(SocialTickerImpl);
